import { META_PIXEL_ID } from "../constants/metaPixel.constants";
import { isTrackingEnabled } from "./trackingEnvironment";

function getCookie(name) {
  if (typeof document === "undefined") return null;

  try {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop().split(";").shift() || null;
    }
  } catch (_error) {
    // Ignore cookie read failures.
  }

  return null;
}

function setCookie(name, value, maxAgeSeconds) {
  if (typeof document === "undefined" || !value) return;

  try {
    const secure =
      typeof window !== "undefined" && window.location?.protocol === "https:"
        ? "; Secure"
        : "";
    document.cookie = `${name}=${value}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax${secure}`;
  } catch (_error) {
    // Ignore cookie write failures.
  }
}

/**
 * Meta _fbc format: fb.{subdomainIndex}.{creationTimeMs}.{fbclid}
 * Persist fbclid from the landing URL so Purchase can still attribute later.
 */
export function ensureMetaClickIdCookie() {
  if (typeof window === "undefined") return null;

  try {
    const existing = getCookie("_fbc");
    if (existing) return existing;

    const params = new URLSearchParams(window.location.search);
    const fbclid = params.get("fbclid");
    if (!fbclid) return null;

    const fbc = `fb.1.${Date.now()}.${fbclid}`;
    // ~90 days, aligned with typical Meta click-id retention.
    setCookie("_fbc", fbc, 90 * 24 * 60 * 60);
    return fbc;
  } catch (_error) {
    return null;
  }
}

export function getMetaClickId() {
  return ensureMetaClickIdCookie() || getCookie("_fbc");
}

export function getMetaBrowserId() {
  return getCookie("_fbp");
}

function normalizeEmail(email) {
  if (!email || typeof email !== "string") return undefined;
  const normalized = email.trim().toLowerCase();
  return normalized || undefined;
}

function normalizePhone(phone) {
  if (phone == null) return undefined;
  const digits = String(phone).replace(/\D/g, "");
  return digits || undefined;
}

function safeFbq(...args) {
  if (!isTrackingEnabled()) return;

  try {
    ensureMetaPixelBootstrap();
    if (typeof window.fbq === "function") {
      window.fbq(...args);
    }
  } catch (_error) {
    // Tracking must never affect checkout UX.
  }
}

/**
 * Installs the Meta Pixel stub immediately (queues calls) and loads fbevents.js.
 * Safe to call multiple times. Init + PageView happen once when PIXEL_ID is known.
 */
export function ensureMetaPixelBootstrap(pixelId = META_PIXEL_ID) {
  if (!isTrackingEnabled() || typeof window === "undefined") return;
  if (typeof document === "undefined") return;

  ensureMetaClickIdCookie();

  const id = String(
    pixelId || window.__CNC_META_PIXEL_ID__ || META_PIXEL_ID || ""
  ).trim();

  // Without a Pixel ID, rely on GTM (if present). Do not load a second copy.
  if (!id) return;

  if (!window.fbq) {
    !(function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod
          ? n.callMethod.apply(n, arguments)
          : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = "2.0";
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(
      window,
      document,
      "script",
      "https://connect.facebook.net/en_US/fbevents.js"
    );
  }

  if (window.__CNC_META_PIXEL_INITIALIZED__) return;

  window.fbq("init", id);
  window.fbq("track", "PageView");
  window.__CNC_META_PIXEL_INITIALIZED__ = true;
  window.__CNC_META_PIXEL_ID__ = id;
}

function applyAdvancedMatching({ userEmail, userPhone, fbc, fbp } = {}) {
  const id =
    String(META_PIXEL_ID || window.__CNC_META_PIXEL_ID__ || "").trim() || null;
  if (!id || typeof window.fbq !== "function") return;

  const userData = {};
  const em = normalizeEmail(userEmail);
  const ph = normalizePhone(userPhone);
  if (em) userData.em = em;
  if (ph) userData.ph = ph;
  if (fbc) userData.fbc = fbc;
  if (fbp) userData.fbp = fbp;

  if (!Object.keys(userData).length) return;

  try {
    // Re-init with the same Pixel ID updates Advanced Matching context.
    window.fbq("init", id, userData);
  } catch (_error) {
    // Tracking must never affect checkout UX.
  }
}

function safeDataLayerPush(payload) {
  if (!isTrackingEnabled()) return;

  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  } catch (_error) {
    // Tracking must never affect checkout UX.
  }
}

export function trackMetaInitiateCheckout({
  value,
  currency = "INR",
  contentIds,
  contentName,
}) {
  const ids = Array.isArray(contentIds)
    ? contentIds.filter(Boolean).map(String)
    : [contentIds].filter(Boolean).map(String);

  const fbc = getMetaClickId();
  const fbp = getMetaBrowserId();

  const params = {
    value: Number(value) || 0,
    currency,
    content_ids: ids,
    content_name: contentName,
    content_type: "product",
    num_items: 1,
  };

  if (fbc) params.fbc = fbc;
  if (fbp) params.fbp = fbp;

  safeFbq("track", "InitiateCheckout", params);
}

export function trackMetaPurchase({
  value,
  currency = "INR",
  contentIds,
  contentName,
  orderId,
  userEmail,
  userPhone,
}) {
  const ids = Array.isArray(contentIds)
    ? contentIds.filter(Boolean).map(String)
    : [contentIds].filter(Boolean).map(String);

  const fbc = getMetaClickId();
  const fbp = getMetaBrowserId();

  applyAdvancedMatching({ userEmail, userPhone, fbc, fbp });

  const params = {
    value: Number(value) || 0,
    currency,
    content_ids: ids,
    content_name: contentName,
    content_type: "product",
    num_items: 1,
  };

  if (fbc) params.fbc = fbc;
  if (fbp) params.fbp = fbp;
  if (orderId) params.event_id = String(orderId);

  if (orderId) {
    safeFbq("track", "Purchase", params, { eventID: String(orderId) });
    return;
  }

  safeFbq("track", "Purchase", params);
}

export { safeDataLayerPush, getCookie };
